import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Field,
  Modal,
  PageHeader,
  Skeleton,
} from '@catering/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail } from 'lucide-react';
import { type FormEvent, type JSX, useState } from 'react';

import { hasPermission } from '../../auth/has-permission';
import { useCurrentUser } from '../../auth/use-current-user';
import { mapEmailSettingsError, mapEmailTestError } from './email-settings-api-errors';
import {
  type EmailSecurity,
  type EmailSettings,
  getEmailSettings,
  sendTestEmail,
  updateEmailSettings,
} from './email-settings.api';
import {
  connectionChanged,
  EMPTY_EMAIL_FORM,
  type EmailSettingsFormErrors,
  type EmailSettingsFormValues,
  formValuesFromSettings,
  isValidEmail,
  suggestedPort,
  toUpdateInput,
  validateEmailSettingsForm,
} from './email-settings-form-schema';

const SECURITY_LABELS: Record<EmailSecurity, string> = {
  NONE: 'Sin cifrado',
  STARTTLS: 'STARTTLS',
  SSL_TLS: 'SSL/TLS',
};

const SECURITY_OPTIONS = Object.keys(SECURITY_LABELS) as EmailSecurity[];

/** `Field` is compiled with `exactOptionalPropertyTypes`: an absent error must be an absent prop. */
function errorProp(error: string | undefined): { error?: string } {
  return error === undefined ? {} : { error };
}

function StatusBanner({ settings }: { settings: EmailSettings }): JSX.Element {
  const { effectiveSource, pendingEmailCount } = settings;

  return (
    <div className="flex flex-col gap-3">
      {effectiveSource === 'app' ? (
        <Alert tone="info">Se envía con la configuración de la app.</Alert>
      ) : null}
      {effectiveSource === 'env' ? (
        <Alert tone="info">
          Se envía con la configuración del servidor. Habilitá la configuración de la app para
          reemplazarla.
        </Alert>
      ) : null}
      {effectiveSource === 'none' ? (
        <Alert tone="warning" title="No hay envío de correo configurado">
          {pendingEmailCount === 1
            ? '1 correo en espera de salir.'
            : `${pendingEmailCount} correos en espera de salir.`}{' '}
          Se enviarán apenas haya una configuración habilitada.
        </Alert>
      ) : null}
      {settings.hasPassword && !settings.passwordReadable ? (
        <Alert tone="warning" title="No se puede leer la contraseña guardada">
          La contraseña guardada no se puede leer porque cambió la clave del servidor. Volvé a
          ingresarla y guardá.
        </Alert>
      ) : null}
    </div>
  );
}

/**
 * `email-settings/spec.md` — "Correo saliente" screen. The hub card is gated
 * on `email.config`; the page double-checks it because a direct URL must not
 * render the form (same reasoning as `BillingConceptConfigPage`).
 */
export function EmailSettingsPage(): JSX.Element {
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<EmailSettingsFormValues | null>(null);
  const [errors, setErrors] = useState<EmailSettingsFormErrors>({});
  const [confirming, setConfirming] = useState(false);
  // A successful test vouches for the saved connection until a save changes it.
  const [testedOk, setTestedOk] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testToError, setTestToError] = useState<string | undefined>();

  const canConfigure = hasPermission(currentUser.data, 'email.config');
  const query = useQuery({
    queryKey: ['email-settings'],
    queryFn: getEmailSettings,
    enabled: canConfigure,
  });

  const settings = query.data;
  const values = draft ?? (settings === undefined ? EMPTY_EMAIL_FORM : formValuesFromSettings(settings));

  const saveMutation = useMutation({
    mutationFn: updateEmailSettings,
    onSuccess: async () => {
      if (settings !== undefined && connectionChanged(values, settings)) {
        setTestedOk(false);
      }
      setConfirming(false);
      setDraft(null);
      setErrors({});
      await queryClient.invalidateQueries({ queryKey: ['email-settings'] });
    },
    onError: () => setConfirming(false),
  });

  const testMutation = useMutation({
    mutationFn: sendTestEmail,
    onSuccess: (result) => setTestedOk(result.ok),
    onError: () => setTestedOk(false),
  });

  function edit(patch: Partial<EmailSettingsFormValues>): void {
    saveMutation.reset();
    setDraft({ ...values, ...patch });
  }

  function changeSecurity(security: EmailSecurity): void {
    const followsSuggestion =
      values.port.trim() === '' || values.port === String(suggestedPort(values.security));

    edit({ security, ...(followsSuggestion ? { port: String(suggestedPort(security)) } : {}) });
  }

  function submit(event: FormEvent): void {
    event.preventDefault();

    if (settings === undefined) {
      return;
    }

    const found = validateEmailSettingsForm(values, { hasStoredPassword: settings.hasPassword });
    setErrors(found);

    if (Object.keys(found).length > 0) {
      return;
    }

    const needsConfirmation =
      values.enabled && !testedOk && (!settings.enabled || connectionChanged(values, settings));

    if (needsConfirmation) {
      setConfirming(true);

      return;
    }

    saveMutation.mutate(toUpdateInput(values));
  }

  function submitTest(event: FormEvent): void {
    event.preventDefault();

    if (!isValidEmail(testTo)) {
      setTestToError('Ingresá un correo válido.');

      return;
    }

    setTestToError(undefined);
    testMutation.mutate(testTo.trim());
  }

  if (currentUser.isPending) {
    return <Skeleton className="h-40 w-full" />;
  }

  if (!canConfigure) {
    return (
      <Alert tone="danger" title="Sección restringida">
        No tenés permiso para acceder a la configuración del correo saliente.
      </Alert>
    );
  }

  const testResult = testMutation.data;

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={Mail}
        title="Correo saliente"
        description="Configurá el servidor de correo con el que el sistema envía correos (invitaciones, avisos y comprobantes)."
      />

      {query.isError ? (
        <Alert tone="danger" title="No pudimos cargar la configuración de correo">
          <div className="flex items-center justify-between gap-4">
            <span>Ocurrió un error al buscar. Intentá de nuevo.</span>
            <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {query.isPending ? <Skeleton className="h-40 w-full" /> : null}

      {settings !== undefined ? (
        <>
          <StatusBanner settings={settings} />

          <form className="flex max-w-2xl flex-col gap-4" onSubmit={submit} noValidate>
            {saveMutation.isError ? (
              <Alert tone="danger">{mapEmailSettingsError(saveMutation.error)}</Alert>
            ) : null}

            <Checkbox
              label="Habilitar envío de correo"
              hint="Si está deshabilitado, el sistema usa la configuración del entorno (si existe)."
              checked={values.enabled}
              onChange={(event) => edit({ enabled: event.target.checked })}
            />

            <Field
              label="Servidor"
              name="host"
              value={values.host}
              {...errorProp(errors.host)}
              onChange={(event) => edit({ host: event.target.value })}
            />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="email-security" className="text-sm font-medium text-slate-900">
                  Seguridad
                </label>
                <select
                  id="email-security"
                  value={values.security}
                  onChange={(event) => changeSecurity(event.target.value as EmailSecurity)}
                  className="h-11 rounded-md border border-slate-300 px-3 text-sm"
                >
                  {SECURITY_OPTIONS.map((security) => (
                    <option key={security} value={security}>
                      {SECURITY_LABELS[security]}
                    </option>
                  ))}
                </select>
              </div>

              <Field
                label="Puerto"
                name="port"
                type="number"
                value={values.port}
                hint={`Puerto habitual: ${suggestedPort(values.security)}`}
                {...errorProp(errors.port)}
                onChange={(event) => edit({ port: event.target.value })}
              />
            </div>

            {values.security === 'NONE' ? (
              <Alert tone="warning">
                Sin cifrado, las credenciales y el contenido de los correos viajan sin cifrar por la
                red. Usalo solo en redes de confianza.
              </Alert>
            ) : null}

            <Field
              label="Usuario"
              name="username"
              autoComplete="off"
              value={values.username}
              onChange={(event) => edit({ username: event.target.value })}
            />

            <div className="flex flex-col gap-2">
              <Field
                label="Contraseña"
                name="password"
                type="password"
                autoComplete="new-password"
                value={values.password}
                disabled={values.clearPassword}
                hint={
                  settings.hasPassword
                    ? 'Dejar en blanco para conservar la contraseña guardada.'
                    : 'Todavía no hay una contraseña guardada.'
                }
                {...errorProp(errors.password)}
                onChange={(event) => edit({ password: event.target.value })}
              />
              {settings.hasPassword ? (
                <div className="flex items-center gap-3">
                  {values.password === '' && !values.clearPassword ? (
                    <Badge variant="neutral">Sin cambios</Badge>
                  ) : null}
                  <Checkbox
                    label="Quitar contraseña"
                    checked={values.clearPassword}
                    onChange={(event) =>
                      edit({ clearPassword: event.target.checked, password: '' })
                    }
                  />
                </div>
              ) : null}
            </div>

            <Field
              label="Remitente (nombre)"
              name="fromName"
              value={values.fromName}
              onChange={(event) => edit({ fromName: event.target.value })}
            />

            <Field
              label="Remitente (correo)"
              name="fromAddress"
              type="email"
              value={values.fromAddress}
              {...errorProp(errors.fromAddress)}
              onChange={(event) => edit({ fromAddress: event.target.value })}
            />

            <Field
              label="Responder a (opcional)"
              name="replyTo"
              type="email"
              value={values.replyTo}
              {...errorProp(errors.replyTo)}
              onChange={(event) => edit({ replyTo: event.target.value })}
            />

            <div className="flex items-center gap-3">
              <Button type="submit" loading={saveMutation.isPending}>
                Guardar
              </Button>
              {settings.updatedBy !== null && settings.updatedAt !== null ? (
                <span className="text-xs text-slate-500">
                  Última modificación: {settings.updatedBy.fullName},{' '}
                  {new Date(settings.updatedAt).toLocaleString('es-AR')}
                </span>
              ) : null}
            </div>
          </form>

          <form className="flex max-w-2xl flex-col gap-3 border-t border-slate-200 pt-6" onSubmit={submitTest} noValidate>
            <h2 className="text-base font-semibold text-slate-900">Correo de prueba</h2>
            <p className="text-sm text-slate-500">
              La prueba usa la configuración guardada (esté habilitada o no), no lo que ves en el
              formulario. Guardá antes de probar.
            </p>

            <Field
              label="Enviar a"
              name="testTo"
              type="email"
              value={testTo}
              {...errorProp(testToError)}
              onChange={(event) => setTestTo(event.target.value)}
            />

            <div>
              <Button type="submit" variant="secondary" loading={testMutation.isPending}>
                Enviar correo de prueba
              </Button>
            </div>

            {testMutation.isError ? (
              <Alert tone="danger">{mapEmailTestError(testMutation.error)}</Alert>
            ) : null}

            {testResult?.ok === true ? (
              <Alert tone="success">
                Correo de prueba enviado ({testResult.source === 'app' ? 'configuración de la app' : 'entorno'}).
              </Alert>
            ) : null}

            {testResult?.ok === false ? (
              <Alert tone="danger" title="No se pudo enviar el correo de prueba">
                <p>
                  <span className="font-mono">{testResult.error.code}</span>
                  {testResult.error.responseCode === undefined
                    ? ''
                    : ` (respuesta ${testResult.error.responseCode})`}
                </p>
                <p className="break-words">{testResult.error.message}</p>
              </Alert>
            ) : null}
          </form>
        </>
      ) : null}

      {confirming ? (
        <Modal
          open
          onClose={() => setConfirming(false)}
          title="Habilitar sin probar"
          className="max-w-md"
        >
          <div className="flex flex-col gap-4">
            <p className="text-sm text-slate-700">
              Todavía no probaste esta configuración. Si los datos son incorrectos, los correos no
              van a salir. ¿Querés habilitarla igual?
            </p>
            <div className="flex items-center gap-3">
              <Button
                loading={saveMutation.isPending}
                onClick={() => saveMutation.mutate(toUpdateInput(values))}
              >
                Habilitar de todos modos
              </Button>
              <Button variant="secondary" onClick={() => setConfirming(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}
